export type CloudSyncStatus = 'connected' | 'syncing' | 'synced' | 'local_only' | 'error';

class CloudSyncService {
  private status: CloudSyncStatus = 'local_only';
  private listeners: ((status: CloudSyncStatus, lastSyncTime: Date | null) => void)[] = [];
  private lastSyncTime: Date | null = null;
  private isSyncing = false;

  constructor() {
    // Check if cloud backend is accessible on startup
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        this.checkCloudConnection();
      }, 1000);
    }
  }

  public subscribe(listener: (status: CloudSyncStatus, lastSyncTime: Date | null) => void) {
    this.listeners.push(listener);
    listener(this.status, this.lastSyncTime);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notify() {
    this.listeners.forEach(l => l(this.status, this.lastSyncTime));
  }

  public getStatus(): CloudSyncStatus {
    return this.status;
  }

  public getLastSyncTime(): Date | null {
    return this.lastSyncTime;
  }

  public async checkCloudConnection(): Promise<boolean> {
    try {
      const res = await fetch('/api/sync', { method: 'GET', headers: { 'Accept': 'application/json' } });
      if (res.ok) {
        this.status = 'connected';
        this.notify();
        return true;
      } else {
        this.status = 'local_only';
        this.notify();
        return false;
      }
    } catch {
      this.status = 'local_only';
      this.notify();
      return false;
    }
  }

  public async pushAll(payload: {
    audits?: any[];
    keywordProjects?: any[];
    contentPlans?: any[];
    onPageProjects?: any[];
  }): Promise<boolean> {
    if (this.isSyncing) return false;
    this.isSyncing = true;
    this.status = 'syncing';
    this.notify();

    try {
      const res = await fetch('/api/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        this.status = 'synced';
        this.lastSyncTime = new Date();
        this.notify();
        this.isSyncing = false;
        return true;
      } else {
        this.status = 'local_only';
        this.notify();
        this.isSyncing = false;
        return false;
      }
    } catch {
      this.status = 'local_only';
      this.notify();
      this.isSyncing = false;
      return false;
    }
  }

  public async pullAll(): Promise<{
    audits?: any[];
    keywordProjects?: any[];
    contentPlans?: any[];
    onPageProjects?: any[];
  } | null> {
    try {
      const res = await fetch('/api/sync', { method: 'GET' });
      if (!res.ok) return null;
      const data = await res.json();
      this.status = 'synced';
      this.lastSyncTime = new Date();
      this.notify();
      return data;
    } catch {
      return null;
    }
  }

  public async saveAuditToCloud(audit: any): Promise<boolean> {
    try {
      const res = await fetch('/api/audits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(audit)
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  public async deleteAuditFromCloud(id: string): Promise<boolean> {
    try {
      const res = await fetch(`/api/audits?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      return res.ok;
    } catch {
      return false;
    }
  }

  public async saveKeywordProjectToCloud(project: any): Promise<boolean> {
    try {
      const res = await fetch('/api/keywords', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(project)
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  public async saveContentPlanToCloud(plan: any): Promise<boolean> {
    try {
      const res = await fetch('/api/content-plans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(plan)
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  public async saveOnPageProjectToCloud(project: any): Promise<boolean> {
    try {
      const res = await fetch('/api/on-page', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(project)
      });
      return res.ok;
    } catch {
      return false;
    }
  }
}

export const cloudSyncService = new CloudSyncService();
